import { useCallback, useContext, useMemo } from 'react';
import * as L from 'leaflet';
import { GeofenceTooltipSetterContext } from './GeofenceTooltipContextProvider';
import { FillHoverOpacity, FillRegularOpacity } from './GeofenceLayerGroup';
import { isGeofenceProperties } from '@customer/store/apis/interactionsApi';

export const useGeofenceTooltipEventHandlers = (): L.LeafletEventHandlerFnMap => {
  const setTooltipProperties = useContext(GeofenceTooltipSetterContext);

  const onMouseOverGeofence = useCallback<L.LeafletMouseEventHandlerFn>(
    ({ propagatedFrom }: L.LeafletMouseEvent): void => {
      if (propagatedFrom instanceof L.Polygon || propagatedFrom instanceof L.Circle) {
        propagatedFrom.setStyle({ fillOpacity: FillHoverOpacity });
        if (isGeofenceProperties(propagatedFrom.feature?.properties)) {
          const properties = propagatedFrom.feature.properties;
          // Only update the properties if they are different from the current state.
          setTooltipProperties?.(state =>
            state && state.id === properties.id ? state : properties,
          );
        }
      }
    },
    [setTooltipProperties],
  );

  const onMouseOutGeofence = useCallback<L.LeafletMouseEventHandlerFn>(
    ({ propagatedFrom }: L.LeafletMouseEvent): void => {
      if (propagatedFrom instanceof L.Polygon || propagatedFrom instanceof L.Circle) {
        propagatedFrom.setStyle({ fillOpacity: FillRegularOpacity });
        if (isGeofenceProperties(propagatedFrom.feature?.properties)) {
          const properties = propagatedFrom.feature.properties;
          // Only clear the properties if cursor is leaving the current geofence
          setTooltipProperties?.(state =>
            state && state.id === properties.id ? undefined : state,
          );
        }
      }
    },
    [setTooltipProperties],
  );

  const eventHandlers = useMemo<L.LeafletEventHandlerFnMap>(
    () => ({
      mouseover: onMouseOverGeofence,
      mouseout: onMouseOutGeofence,
    }),
    [onMouseOverGeofence, onMouseOutGeofence],
  );

  return eventHandlers;
};
